/**
 * TrafficMonitorPanel — live view of OPBridge's inbound MIDI + OSC rings.
 *
 * Diagnostic modal (2026-08-11, the "PWA traffic-monitor dashboard" QUEUED
 * item). All the connect/backfill/reconnect logic lives in
 * `opbridge/trafficMonitor.ts` — this component is a thin renderer over
 * its two log signals. `settings/store.ts`'s `openTrafficMonitor`/
 * `closeTrafficMonitor` already call that module's `connect()`/
 * `disconnect()`, so this component doesn't manage the connection itself.
 *
 * Newest-first rendering (not append-to-bottom-and-scroll) — simplest
 * correct behavior for a live-tailing diagnostic list with no scroll-
 * position management needed.
 */

import { For, Show, onMount, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { shellPortal } from '../layout/shellPortal.ts';
import { closeTrafficMonitor } from '../settings/store.ts';
import {
  trafficMidiLog, trafficOscLog, trafficMidiStatus, trafficOscStatus,
  type TrafficStreamStatus,
} from '../opbridge/trafficMonitor.ts';
import type { TrafficMidiEntry, TrafficOscEntry } from '../opbridge/httpClient.ts';

const STATUS_LABEL: Record<TrafficStreamStatus, string> = {
  idle: 'idle', connecting: 'connecting…', connected: 'live', error: 'reconnecting…',
};

const MIDI_KIND_LABEL: Record<number, string> = {
  0x8: 'Note Off', 0x9: 'Note On', 0xA: 'Poly AT', 0xB: 'CC',
  0xC: 'PC', 0xD: 'Chan AT', 0xE: 'Pitch Bend', 0xF: 'System',
};

/** Human-readable one-liner for a raw MIDI byte triple. Channel shown
 *  1-based (universal MIDI convention when talking ABOUT a message —
 *  "channel 3" — independent of this app's own internal item-channel
 *  field inconsistencies elsewhere). */
function describeMidi(bytes: number[]): string {
  const status = bytes[0] ?? 0;
  const hi = (status >> 4) & 0x0F;
  const ch = (status & 0x0F) + 1;
  const kind = MIDI_KIND_LABEL[hi] ?? `0x${status.toString(16)}`;
  if (hi === 0xF) return `${kind} [${bytes.map((b) => `0x${b.toString(16).padStart(2, '0')}`).join(' ')}]`;
  const d1 = bytes[1];
  const d2 = bytes[2];
  if (hi === 0x9 || hi === 0x8) return `${kind} ch${ch} note ${d1 ?? '?'} vel ${d2 ?? '?'}`;
  if (hi === 0xB) return `${kind} ch${ch} CC${d1 ?? '?'}=${d2 ?? '?'}`;
  if (hi === 0xC) return `${kind} ch${ch} program ${d1 ?? '?'}`;
  if (hi === 0xE) return `${kind} ch${ch} ${(((d2 ?? 0) << 7) | (d1 ?? 0))}`;
  return `${kind} ch${ch} ${d1 ?? ''} ${d2 ?? ''}`.trim();
}

function fmtTime(us: number): string {
  const d = new Date(us / 1000);
  return d.toLocaleTimeString(undefined, { hour12: false }) + `.${String(Math.floor((us / 1000) % 1000)).padStart(3, '0')}`;
}

function StatusPill(props: { status: TrafficStreamStatus }) {
  return (
    <span class="traffic-status-pill" data-status={props.status}>
      <span class="traffic-status-dot" aria-hidden="true" />
      {STATUS_LABEL[props.status]}
    </span>
  );
}

function MidiRow(props: { entry: TrafficMidiEntry }) {
  return (
    <div class="traffic-row" data-filtered={props.entry.filtered ? 'true' : 'false'}>
      <span class="traffic-row-time">{fmtTime(props.entry.timestampUs)}</span>
      <span class="traffic-row-src">{props.entry.port}</span>
      <span class="traffic-row-desc">{describeMidi(props.entry.bytes)}</span>
      <Show when={props.entry.filtered}><span class="traffic-row-filtered">filtered</span></Show>
    </div>
  );
}

function OscRow(props: { entry: TrafficOscEntry }) {
  return (
    <div class="traffic-row" data-filtered={props.entry.filtered ? 'true' : 'false'}>
      <span class="traffic-row-time">{fmtTime(props.entry.timestampUs)}</span>
      <span class="traffic-row-src">{props.entry.source}</span>
      <span class="traffic-row-desc">{props.entry.address} <span class="traffic-row-argtypes">({props.entry.argTypes || 'no args'})</span></span>
      <Show when={props.entry.filtered}><span class="traffic-row-filtered">filtered</span></Show>
    </div>
  );
}

export function TrafficMonitorPanel() {
  // Own Escape handler, same shape as ConnectedDevices.tsx — this panel
  // stacks over Settings (opened via its shortcut row, same as Connect),
  // so SettingsPanel.tsx's own Escape guard is told to stand down while
  // this is open rather than closing Settings underneath it.
  function onKey(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    closeTrafficMonitor();
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  return (
    <Portal mount={shellPortal() ?? document.body}>
      <div class="sk-scrim" onClick={closeTrafficMonitor} />
      <div
        class="sk-modal"
        data-size="sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Traffic Monitor"
      >
        <header class="sk-mhead">
          <span class="sk-mtitle">Traffic Monitor</span>
          <button
            type="button"
            class="sk-mclose"
            onClick={closeTrafficMonitor}
            title="Close (Esc)"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div class="sk-mbody traffic-monitor-body">
          <p class="sk-ssub">
            Live inbound MIDI + OSC seen by OPBridge — diagnostic only, last {200} events per stream.
            Closing this panel stops the streams; reopening starts fresh.
          </p>
          <div class="traffic-monitor-cols">
            <section class="traffic-col">
              <div class="traffic-col-head">
                <span class="traffic-col-title">MIDI</span>
                <StatusPill status={trafficMidiStatus()} />
              </div>
              <div class="traffic-col-list">
                <Show when={trafficMidiLog().length > 0} fallback={<p class="traffic-col-empty">No MIDI seen yet.</p>}>
                  <For each={[...trafficMidiLog()].reverse()}>
                    {(entry) => <MidiRow entry={entry} />}
                  </For>
                </Show>
              </div>
            </section>
            <section class="traffic-col">
              <div class="traffic-col-head">
                <span class="traffic-col-title">OSC</span>
                <StatusPill status={trafficOscStatus()} />
              </div>
              <div class="traffic-col-list">
                <Show when={trafficOscLog().length > 0} fallback={<p class="traffic-col-empty">No OSC seen yet.</p>}>
                  <For each={[...trafficOscLog()].reverse()}>
                    {(entry) => <OscRow entry={entry} />}
                  </For>
                </Show>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Portal>
  );
}
